/** `snippets/article-gallery.liquid` — `<article-gallery>`: an article-body
 *  gallery rendered from a `[gallery slides="..."]` shortcode. The main image
 *  area is a Swiper that crossfades between slides on a timer; the thumbnail
 *  strip below is a second, synced Swiper that scrolls horizontally (free
 *  mode) and jumps the main slideshow when a thumbnail is selected. Swiper's
 *  Thumbs module keeps the active thumb (`swiper-slide-thumb-active`) in sync
 *  with the main slide, which drives the active/inactive thumb styling.
 */

import Swiper from 'swiper'
import { Autoplay, EffectFade, FreeMode, Navigation, Thumbs } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/effect-fade'

const MAIN_SELECTOR = '[data-article-gallery-main]'
const THUMBS_SELECTOR = '[data-article-gallery-thumbs]'
const PREV_SELECTOR = '[data-article-gallery-prev]'
const NEXT_SELECTOR = '[data-article-gallery-next]'
const SLIDE_SELECTOR = '.swiper-slide'
const AUTOPLAY_DELAY_MS = 5000
const FADE_DURATION_MS = 500

class ArticleGallery extends HTMLElement {
  #mainSwiper
  #thumbsSwiper

  connectedCallback() {
    const mainEl = this.querySelector(MAIN_SELECTOR)
    if (!mainEl) return

    const slideCount = mainEl.querySelectorAll(`.swiper-wrapper > ${SLIDE_SELECTOR}`).length
    const thumbsEl = this.querySelector(THUMBS_SELECTOR)
    const prevEl = this.querySelector(PREV_SELECTOR)
    const nextEl = this.querySelector(NEXT_SELECTOR)

    // Autoplay delay (ms) is set per section via `data-slideshow-timeout`; fall
    // back to the default when the attribute is missing or invalid.
    const autoplayDelay =
      Number.parseInt(this.dataset.slideshowTimeout ?? '', 10) || AUTOPLAY_DELAY_MS

    if (thumbsEl) {
      this.#thumbsSwiper = new Swiper(thumbsEl, {
        modules: [FreeMode],
        slidesPerView: 'auto',
        // 10px gap on mobile (Figma 6461:20477), 15px from tablet up.
        spaceBetween: 10,
        watchSlidesProgress: true,
        freeMode: true,
        breakpoints: {
          769: { spaceBetween: 15 },
        },
      })
    }

    this.#mainSwiper = new Swiper(mainEl, {
      modules: [Autoplay, EffectFade, Navigation, Thumbs],
      slidesPerView: 1,
      spaceBetween: 0,
      loop: slideCount > 1,
      speed: FADE_DURATION_MS,
      effect: 'fade',
      fadeEffect: { crossFade: true },
      allowTouchMove: slideCount > 1,
      autoplay:
        slideCount > 1
          ? {
              delay: autoplayDelay,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          : false,
      navigation: prevEl && nextEl ? { prevEl, nextEl } : false,
      thumbs: this.#thumbsSwiper ? { swiper: this.#thumbsSwiper } : undefined,
    })
  }

  disconnectedCallback() {
    this.#mainSwiper?.destroy(true, true)
    this.#thumbsSwiper?.destroy(true, true)
    this.#mainSwiper = undefined
    this.#thumbsSwiper = undefined
  }
}

if (!customElements.get('article-gallery')) {
  customElements.define('article-gallery', ArticleGallery)
}
